import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Save, Shuffle, Trash2, Plus, Trophy, X } from "lucide-react";
import { F, Grid2, Drawer, DrawerActions } from "@/components/admin/CMS";

export default function RetoAdmin() {
  const [tab, setTab] = useState("participants");
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [winners, setWinners] = useState([]);
  const [draw, setDraw] = useState(null); // {tier, candidate}

  const loadAll = async () => {
    try {
      const [c, e, s, w] = await Promise.all([
        api.get("/admin/reto/config"),
        api.get("/admin/reto/entries", { params: { ...(filter !== "all" ? { correct: Number(filter) } : {}), ...(q ? { q } : {}) } }),
        api.get("/admin/reto/stats"),
        api.get("/admin/reto/winners"),
      ]);
      setCfg(c.data);
      setEntries(e.data);
      setStats(s.data);
      setWinners(w.data);
    } catch (err) {
      toast.error("Error cargando datos del reto");
    }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => loadAll(), 250);
    return () => clearTimeout(t);
  }, [filter, q]); // eslint-disable-line

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/reto/config", cfg);
      toast.success("Configuración guardada. Participaciones re-calificadas.");
      await loadAll();
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setSavingCfg(false); }
  };

  const downloadCSV = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/admin/reto/export`, "_blank");
  };

  const drawWinner = async (tier) => {
    try {
      const r = await api.post(`/admin/reto/draw/${tier}`);
      setDraw({ tier, candidate: r.data.candidate, pool_size: r.data.pool_size });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sortear");
    }
  };
  const confirmWinner = async () => {
    try {
      await api.post("/admin/reto/winners", { tier: draw.tier, entry_id: draw.candidate.id });
      toast.success("Ganador guardado");
      setDraw(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo confirmar");
    }
  };
  const deleteWinner = async (id) => {
    if (!confirm("¿Eliminar este ganador del historial?")) return;
    await api.delete(`/admin/reto/winners/${id}`);
    loadAll();
  };

  const addPrize = () => setCfg({ ...cfg, prizes: [...(cfg.prizes || []), { title: "", description: "" }] });
  const upPrize = (i, k, v) => { const n = [...cfg.prizes]; n[i] = { ...n[i], [k]: v }; setCfg({ ...cfg, prizes: n }); };
  const rmPrize = (i) => setCfg({ ...cfg, prizes: cfg.prizes.filter((_, j) => j !== i) });
  const addSponsor = () => setCfg({ ...cfg, sponsors: [...(cfg.sponsors || []), { name: "" }] });
  const upSponsor = (i, v) => { const n = [...cfg.sponsors]; n[i] = { name: v }; setCfg({ ...cfg, sponsors: n }); };
  const rmSponsor = (i) => setCfg({ ...cfg, sponsors: cfg.sponsors.filter((_, j) => j !== i) });

  if (!cfg) return <div className="text-mp-mute">Cargando…</div>;

  return (
    <div data-testid="reto-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-white">Reto Modo Pro</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {[["participants", "Participantes"], ["winners", "Ganadores"], ["config", "Configuración"]].map(([k, l]) => (
            <button key={k} data-testid={`reto-tab-${k}`} onClick={() => setTab(k)} className={`font-display text-[10px] tracking-[0.24em] uppercase px-3 py-2 border ${tab === k ? "border-mp-orange text-mp-orange" : "border-white/15 text-white/70"}`}>{l}</button>
          ))}
        </div>
      </div>

      {tab === "participants" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 mb-6">
            <Stat label="Total" value={stats?.total || 0} highlight />
            <Stat label="0 aciertos" value={stats?.by_correct?.["0"] || 0} />
            <Stat label="1 acierto" value={stats?.by_correct?.["1"] || 0} />
            <Stat label="2 aciertos" value={stats?.by_correct?.["2"] || 0} />
            <Stat label="3 aciertos" value={stats?.by_correct?.["3"] || 0} />
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
            <div className="flex gap-2 items-center">
              <input data-testid="reto-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o email…" className="bg-mp-off border border-white/10 px-3 py-2 text-sm text-white focus:border-mp-orange outline-none w-72" />
              <select data-testid="reto-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-mp-off border border-white/10 px-3 py-2 text-sm text-white focus:border-mp-orange outline-none">
                <option value="all">Todos</option>
                <option value="0">0 aciertos</option>
                <option value="1">1 acierto</option>
                <option value="2">2 aciertos</option>
                <option value="3">3 aciertos</option>
              </select>
            </div>
            <button data-testid="reto-export" onClick={downloadCSV} className="mp-btn-ghost !py-2 !px-4"><Download size={14}/> Exportar CSV</button>
          </div>

          <div className="border border-white/10">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] tracking-[0.24em] uppercase text-mp-mute font-display">
              <div className="col-span-3">Participante</div>
              <div className="col-span-3">Contacto</div>
              <div className="col-span-4">Respuestas (ep1 · ep2 · ep3)</div>
              <div className="col-span-1 text-center">Aciertos</div>
              <div className="col-span-1 text-right">Enviado</div>
            </div>
            {entries.length === 0 && <div className="p-8 text-mp-mute text-sm">Sin participantes.</div>}
            {entries.map((e) => (
              <div key={e.id} data-testid={`reto-row-${e.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 items-center text-sm">
                <div className="col-span-3">
                  <div className="text-white font-display">{e.full_name}</div>
                  {e.duplicate && <span className="text-mp-orange text-[10px] tracking-[0.2em] uppercase">Duplicado</span>}
                </div>
                <div className="col-span-3 text-white/80">
                  <div>{e.email}</div>
                  <div className="text-mp-mute text-xs">{e.phone}</div>
                </div>
                <div className="col-span-4 text-white/80 text-xs flex flex-wrap gap-2">
                  {["ep1", "ep2", "ep3"].map((k) => {
                    const val = e.answers?.[k];
                    const ok = e.per_episode_correct?.[k];
                    if (!val) return <span key={k} className="px-2 py-1 border border-white/10 text-mp-mute">—</span>;
                    return <span key={k} className={`px-2 py-1 border ${ok ? "border-mp-orange text-mp-orange bg-mp-orange/10" : "border-white/15 text-white"}`}>{val}</span>;
                  })}
                </div>
                <div className="col-span-1 text-center">
                  <span className={`inline-flex min-w-[28px] justify-center px-2 py-1 text-[11px] font-display tracking-[0.1em] ${e.correct_count === 3 ? "bg-mp-orange text-mp-black" : e.correct_count > 0 ? "border border-mp-orange text-mp-orange" : "border border-white/15 text-white/60"}`}>{e.correct_count}</span>
                </div>
                <div className="col-span-1 text-right text-mp-mute text-xs">{new Date(e.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "winners" && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((tier) => {
              const w = winners.find((x) => x.tier === tier);
              return (
                <div key={tier} className={`border p-6 ${tier === 3 ? "border-mp-orange" : "border-white/10"}`}>
                  <div className="mp-eyebrow">{tier === 3 ? "Gran Premio" : `Premio #${tier}`}</div>
                  <div className="mt-2 text-white/70 text-sm">{tier} acierto(s) exacto(s)</div>
                  {w ? (
                    <div className="mt-5">
                      <div className="flex items-center gap-2 text-mp-orange">
                        <Trophy size={16}/>
                        <span className="font-display tracking-[0.2em] uppercase text-xs">Ganador confirmado</span>
                      </div>
                      <div className="mt-2 font-display text-white text-xl">{w.entry_snapshot.full_name}</div>
                      <div className="text-mp-mute text-xs">{w.entry_snapshot.email}</div>
                      <div className="text-mp-mute text-xs">{w.entry_snapshot.phone}</div>
                      <button data-testid={`reto-del-winner-${tier}`} onClick={() => deleteWinner(w.id)} className="mt-4 text-mp-mute hover:text-red-400 text-xs flex items-center gap-1"><Trash2 size={12}/> Eliminar</button>
                    </div>
                  ) : (
                    <button data-testid={`reto-draw-${tier}`} onClick={() => drawWinner(tier)} className="mp-btn-ghost mt-6 !py-2 !px-4"><Shuffle size={14}/> Seleccionar ganador</button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-mp-mute text-xs">Las personas ya ganadoras quedan excluidas de los siguientes sorteos para evitar que una misma persona gane más de un premio.</p>
        </div>
      )}

      {tab === "config" && (
        <div className="space-y-10">
          <section>
            <div className="mp-eyebrow mb-4">Palabras pro correctas (privadas)</div>
            <p className="text-mp-mute text-xs mb-4">Solo visibles en este panel. Nunca se exponen al público ni a las APIs públicas.</p>
            <Grid2>
              <F label="Palabra Pro — Episodio 1"><input data-testid="reto-cfg-ep1" className="mp-input" value={cfg.palabras?.ep1 || ""} onChange={(e) => setCfg({ ...cfg, palabras: { ...cfg.palabras, ep1: e.target.value } })} /></F>
              <F label="Palabra Pro — Episodio 2"><input data-testid="reto-cfg-ep2" className="mp-input" value={cfg.palabras?.ep2 || ""} onChange={(e) => setCfg({ ...cfg, palabras: { ...cfg.palabras, ep2: e.target.value } })} /></F>
              <F label="Palabra Pro — Episodio 3" span={2}><input data-testid="reto-cfg-ep3" className="mp-input" value={cfg.palabras?.ep3 || ""} onChange={(e) => setCfg({ ...cfg, palabras: { ...cfg.palabras, ep3: e.target.value } })} /></F>
            </Grid2>
          </section>

          <section>
            <div className="mp-eyebrow mb-4">Configuración del concurso</div>
            <Grid2>
              <F label="Playlist YouTube URL"><input className="mp-input" value={cfg.youtube_url} onChange={(e) => setCfg({ ...cfg, youtube_url: e.target.value })} /></F>
              <F label="¿Activo?">
                <label className="flex items-center gap-3 pt-4 cursor-pointer">
                  <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-mp-orange w-4 h-4" />
                  <span className="text-white/80 text-sm">Reto habilitado para recibir participaciones</span>
                </label>
              </F>
              <F label="Headline promocional"><input className="mp-input" value={cfg.promo_headline || ""} onChange={(e) => setCfg({ ...cfg, promo_headline: e.target.value })} placeholder="Hasta $1,000 en premios"/></F>
              <F label="Subtexto promocional"><input className="mp-input" value={cfg.promo_sub || ""} onChange={(e) => setCfg({ ...cfg, promo_sub: e.target.value })} placeholder="De nuestros patrocinadores"/></F>
              <F label="Valor objetivo total (USD)"><input type="number" className="mp-input" value={cfg.prize_total_target ?? 0} onChange={(e) => setCfg({ ...cfg, prize_total_target: parseInt(e.target.value || "0") })} /></F>
              <F label="Valor máximo promocional (USD)"><input type="number" className="mp-input" value={cfg.prize_total_cap ?? 0} onChange={(e) => setCfg({ ...cfg, prize_total_cap: parseInt(e.target.value || "0") })} /></F>
              <F label="Publicación del landing (YYYY-MM-DD)"><input className="mp-input" value={cfg.publish_at || ""} onChange={(e) => setCfg({ ...cfg, publish_at: e.target.value })} /></F>
              <F label="Fecha del sorteo (texto)"><input className="mp-input" value={cfg.draw_date || ""} onChange={(e) => setCfg({ ...cfg, draw_date: e.target.value })} /></F>
              <F label="Inicio ISO (ET, ej: 2026-10-13T00:00:00-04:00)"><input className="mp-input" value={cfg.start_at || ""} onChange={(e) => setCfg({ ...cfg, start_at: e.target.value })} /></F>
              <F label="Cierre ISO (ET, ej: 2026-10-31T23:59:00-04:00)"><input className="mp-input" value={cfg.end_at || ""} onChange={(e) => setCfg({ ...cfg, end_at: e.target.value })} /></F>
              <F label="Elegibilidad" span={2}><input className="mp-input" value={cfg.eligibility || ""} onChange={(e) => setCfg({ ...cfg, eligibility: e.target.value })} /></F>
            </Grid2>
          </section>

          <section>
            <div className="mp-eyebrow mb-4">Organizador</div>
            <Grid2>
              <F label="Organizador legal"><input className="mp-input" value={cfg.organizer_name || ""} onChange={(e) => setCfg({ ...cfg, organizer_name: e.target.value })} /></F>
              <F label="Marca"><input className="mp-input" value={cfg.organizer_brand || ""} onChange={(e) => setCfg({ ...cfg, organizer_brand: e.target.value })} /></F>
              <F label="Website"><input className="mp-input" value={cfg.organizer_website || ""} onChange={(e) => setCfg({ ...cfg, organizer_website: e.target.value })} /></F>
              <F label="Instagram"><input className="mp-input" value={cfg.organizer_instagram || ""} onChange={(e) => setCfg({ ...cfg, organizer_instagram: e.target.value })} /></F>
              <F label="Email de contacto" span={2}><input className="mp-input" value={cfg.organizer_email || ""} onChange={(e) => setCfg({ ...cfg, organizer_email: e.target.value })} /></F>
            </Grid2>
          </section>

          <section>
            <div className="mp-eyebrow mb-4">Textos legales</div>
            <F label="Composición de los premios"><textarea className="mp-input" rows={6} value={cfg.prize_composition || ""} onChange={(e) => setCfg({ ...cfg, prize_composition: e.target.value })} /></F>
            <div className="mt-6"><F label="Entrega de premios"><textarea className="mp-input" rows={5} value={cfg.delivery_info || ""} onChange={(e) => setCfg({ ...cfg, delivery_info: e.target.value })} /></F></div>
            <div className="mt-6"><F label="Fotografías de ganadores (consentimiento)"><textarea className="mp-input" rows={4} value={cfg.photo_consent_info || ""} onChange={(e) => setCfg({ ...cfg, photo_consent_info: e.target.value })} /></F></div>
            <div className="mt-6"><F label="Términos adicionales (opcional)"><textarea className="mp-input" rows={5} value={cfg.rules_text || ""} onChange={(e) => setCfg({ ...cfg, rules_text: e.target.value })} /></F></div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="mp-eyebrow">Premios</div>
              <button onClick={addPrize} className="mp-btn-ghost !py-2 !px-3"><Plus size={14}/> Agregar</button>
            </div>
            <div className="space-y-3">
              {(cfg.prizes || []).map((p, i) => (
                <div key={i} className="border border-white/10 p-4 grid grid-cols-12 gap-3">
                  <div className="col-span-4"><input className="mp-input" placeholder="Título" value={p.title || ""} onChange={(e) => upPrize(i, "title", e.target.value)} /></div>
                  <div className="col-span-7"><input className="mp-input" placeholder="Descripción" value={p.description || ""} onChange={(e) => upPrize(i, "description", e.target.value)} /></div>
                  <button onClick={() => rmPrize(i)} className="col-span-1 justify-self-end text-white/60 hover:text-red-400 pt-4"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="mp-eyebrow">Patrocinadores</div>
              <button onClick={addSponsor} className="mp-btn-ghost !py-2 !px-3"><Plus size={14}/> Agregar</button>
            </div>
            <div className="space-y-3">
              {(cfg.sponsors || []).map((s, i) => (
                <div key={i} className="border border-white/10 p-4 grid grid-cols-12 gap-3">
                  <div className="col-span-11"><input className="mp-input" placeholder="Nombre del patrocinador" value={s.name || ""} onChange={(e) => upSponsor(i, e.target.value)} /></div>
                  <button onClick={() => rmSponsor(i)} className="col-span-1 justify-self-end text-white/60 hover:text-red-400 pt-4"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </section>

          <div className="border-t border-white/10 pt-6 flex justify-end">
            <button data-testid="reto-cfg-save" disabled={savingCfg} onClick={saveCfg} className="mp-btn-primary"><Save size={14}/> {savingCfg ? "Guardando…" : "Guardar y recalificar"}</button>
          </div>
        </div>
      )}

      {draw && (
        <Drawer title={`Candidato para ${draw.tier === 3 ? "Gran Premio" : `Premio #${draw.tier}`}`} onClose={() => setDraw(null)}>
          <div className="text-mp-mute text-sm">Candidato seleccionado al azar entre {draw.pool_size} participante(s) elegibles.</div>
          <div className="mt-6 border border-mp-orange p-6 bg-mp-orange/10">
            <div className="mp-eyebrow">Candidato</div>
            <div className="mt-2 font-display text-white text-2xl">{draw.candidate.full_name}</div>
            <div className="text-white/80 text-sm mt-2">{draw.candidate.email}</div>
            <div className="text-mp-mute text-xs mt-1">{draw.candidate.phone}</div>
            <div className="text-mp-orange text-xs mt-2 font-display tracking-[0.2em] uppercase">{draw.candidate.correct_count} acierto(s)</div>
          </div>
          <div className="mt-6 flex gap-3 justify-end">
            <button onClick={() => drawWinner(draw.tier)} className="mp-btn-ghost"><Shuffle size={14}/> Volver a sortear</button>
            <button data-testid="reto-confirm-winner" onClick={confirmWinner} className="mp-btn-primary"><Trophy size={14}/> Confirmar ganador</button>
          </div>
        </Drawer>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-4 ${highlight ? "bg-mp-orange/10" : "bg-mp-black"}`}>
      <div className={`font-display text-3xl ${highlight ? "text-mp-orange" : "text-white"}`}>{value}</div>
      <div className="text-mp-mute text-[10px] tracking-[0.2em] uppercase mt-1 font-display">{label}</div>
    </div>
  );
}
